import { defineStore } from 'pinia';
import {
	OfferModel,
	SortModel,
	SpeciesModel,
	type OfferField,
	CountryUtil,
	Offer,
	UserTypes,
	OfferUtil,
	Species,
	SupAccount,
	Sort
} from '@fibl/oxs-common';
import { useRelatedStore } from './related';
import { useOfferGroupStore } from './offergroup';
import * as SearchRepo from '@/repository/search';
import { useTokenStore } from './token';
import { useSelectOptionStore } from './selectoption';
import { type Search, EAV } from '@fibl/zdb-common';
import * as OfferFieldLinkUtil from '@/util/offer';

export const useOfferFormStore = defineStore('offerForm', {
	state: () => ({
		entity: new OfferModel(),
		species: new SpeciesModel(),
		sort: new SortModel(),
		owner: null as null | UserTypes.AccountSup,
		sortDrawerOpen: false,
		sortResultTarget: -1,
		step: 0,
		offerfields: [] as EAV.Entity<OfferField.OfferField>[],
		mixSpecies: [] as EAV.Entity<Species.Species>[],
		clonedOffer: {} as EAV.Entity<Offer.Offer, Offer.Indexfields>,
		linkOfferfieldIds: [] as string[],
	}),

	getters: {
		isOffergroupsDefinedBySort() {
			return (cc: CountryUtil.CountryCode): boolean => {
				return this.species.countrySpecific[cc]?.offergroupsDictatedBySort || false;
			};
		},
		filledSupCountries(): CountryUtil.CountryCode[] {
			let supCountries = this.supCountries;
			supCountries = supCountries.filter((country) => Object.keys(this.entity.countrySpecific).includes(country) && this.entity.countrySpecific[country]?.status === 'available');
			return supCountries;
		},
		supCountries(): CountryUtil.CountryCode[] {
			if (!this.owner) return [];
			const userData = this.owner.userData as UserTypes.UserDataSup;
			const staticData = this.owner.staticData as UserTypes.StaticDataSup;
			const supData = userData.countries.filter(
				(c) => staticData.countrySpecific?.[c.country]?.autApprovalStatus === 'accepted',
			);
			return supData.filter((c) => !c.deactivated).map((c) => c.country);
		},
		approvedCountries(): CountryUtil.CountryCode[] {
			if (!this.owner) return [];
			return this.owner.userData.countries
				.filter(
					(e) =>
						!e.deactivated &&
						this.owner!.staticData &&
						this.owner!.staticData.countrySpecific &&
						this.owner!.staticData.countrySpecific[e.country]?.autApprovalStatus ===
							'accepted',
				)
				.map((e) => e.country);
		},
		canSave(): boolean {
			return this.step > 0;
		},
		mixSpeciesFilter(): Search.SearchFilter {
			return {
				field: '/data/attributes/key',
				comparison: 'eq',
				value: 'formix',
			};
		},
	},

	actions: {
		getSpeciesRdbCategories() {
			return this.species.rdbCategories;
		},
		getSpeciesOxsCategories(cc: CountryUtil.CountryCode) {
			return this.species.countrySpecific[cc]?.categories || [];
		},
		async loadOfferFieldsForCountries() {
			const filter: SearchRepo.SearchFilter = { $and: [] };
			let cf: SearchRepo.SearchFilter = { $or: [] };
			for (let cc of this.approvedCountries) {
				cf.$or.push({
					field: '/data/availableInCountries',
					comparison: 'eq',
					value: cc,
				});
			}
			cf.$or.push({
				$not: {
					field: '/data',
					comparison: 'exists',
					value: 'availableInCountries',
				},
			});
			filter.$and.push(cf);
			this.offerfields = await SearchRepo.search('offerfield', filter, { limit: 1000 });
		},
		async loadLinkedOfferFieldIds() {
			const filter: SearchRepo.SearchFilter = {
				field: '/data/copyFromCountries',
				comparison: 'eq',
				value: true,
			};
			this.linkOfferfieldIds = await SearchRepo.searchOnlyIds('offerfield', filter, {
				limit: 1000,
			});
		},
		async loadMixSpecies() {
			if (this.mixSpecies.length) return;
			this.mixSpecies = await SearchRepo.search('species', this.mixSpeciesFilter, {
				limit: 50,
			});
		},
		async initExisting(entity: OfferModel) {
			await useSelectOptionStore().loadSpecified(this.approvedCountries);
			if (!this.offerfields.length) await this.loadOfferFieldsForCountries();
			this.step = 1;
			this.entity = entity;
			this.setSpecies(entity.species);
			await this.setSort(entity.sort);
			const ccs = Object.keys(entity.countrySpecific) as CountryUtil.CountryCode[];
			ccs.forEach((cc) => this.initDynamicFieldData(cc));
		},
		async initNew() {
			const tokenStore = useTokenStore();
			this.entity = new OfferModel();
			this.entity.owner = tokenStore.accountId;
			const supAcc = new SupAccount();
			supAcc.fromStorage(tokenStore.main.details);
			this.setOwner(supAcc);
			this.species = new SpeciesModel();
			this.sort = new SortModel();
			this.step = 0;
			if (!this.offerfields.length) await this.loadOfferFieldsForCountries();
			return this.entity;
		},
		setSpecies(speciesId: string) {
			if (!speciesId) {
				this.entity.species = '';
				this.species = new SpeciesModel();
				return;
			}
			if (speciesId === this.species?.getId()) return;
			this.entity.species = speciesId;
			const ogStore = useOfferGroupStore();
			ogStore.loadForSpecies(speciesId);
			const related = useRelatedStore();
			let species = related.byId(speciesId);
			if (species) {
				this.species = new SpeciesModel();
				this.species.fromStorage(species);
				this.setOfferType();
			} else {
				related.addEntity(speciesId).then((ent) => {
					this.species = new SpeciesModel();
					this.species.fromStorage(ent);
					this.setOfferType();
				});
			}
		},
		setOfferType() {
			this.entity.type = 'normal';
			if (this.species.attributes.length > 0) {
				for (let attribute of this.species.attributes) {
					if (attribute.key === 'formix') {
						this.entity.type = 'mix';
						break;
					}
				}
			}
		},
		async setSort(sortId?: string) {
			if (!sortId) {
				this.entity.sort = undefined;
				return;
			}
			this.entity.sort = sortId;
			const related = useRelatedStore();
			let sort = await related.addEntity(sortId);
			if (!sort) {
				console.error('expected sort in related store: ' + sortId);
				return;
			}
			this.sort = new SortModel();
			this.sort.fromStorage(sort);
			if (sort.data.species !== this.entity.species) {
				console.warn(
					`sort triggering species change from ${this.entity.species} to ${sort.data.species}`,
				);
				this.setSpecies(this.sort.species);
			}
			// for (let country of this.approvedCountries) {
			// 	let ogBySort = this.isOffergroupsDefinedBySort(country);
			// 	if (ogBySort) {
			// 		this.entity.getCountryData(country).groups = this.sort
			// 			.getCountryData(country)
			// 			.groups.slice(0);
			// 		console.log('set og by sort', this.sort.getCountryData(country).groups.length);
			// 	}
			// }
		},
		setOwner(sup: UserTypes.AccountSup) {
			this.owner = sup;
		},
		getDynamicFieldsForCountry(cc: CountryUtil.CountryCode) {
			return this.offerfields.filter((f) => {
				if (f.data.availableInCountries.length && !f.data.availableInCountries.includes(cc))
					return false;
				if (f.data.species?.length && !f.data.species.includes(this.entity.species))
					return false;
				if (
					f.data.typeOfMaterials?.length &&
					!f.data.typeOfMaterials.includes(this.entity.typeOfMaterial)
				)
					return false;
				if (
					f.data.rdbCat?.length &&
					!f.data.rdbCat.some((cat) => this.species.rdbCategories.includes(cat))
				)
					return false;
				if (
					f.data.oxsCat?.length &&
					!f.data.oxsCat.some((cat) =>
						this.species.countrySpecific[cc]?.categories.includes(cat),
					)
				)
					return false;
				return true;
			});
		},
		initDynamicFieldData(cc: CountryUtil.CountryCode) {
			let cd = this.entity.getCountryData(cc);
			let fields = this.getDynamicFieldsForCountry(cc);
			let existing = cd.dynamicFields || [];
			let newEntries = fields.map((f) => {
				let i: Offer.DynamicFieldEntry;
				switch (f.data.type) {
					default: {
						throw new Error('store.offer.unknownFieldType: ' + f.data.type);
					}
					case 'datefield': {
						i = { field: f.id, dateValue: '', inputType: 'date' };
						break;
					}
					case 'multiselect': {
						i = { field: f.id, inputType: 'multiselect', multiselectValue: [] };
						break;
					}
					case 'select': {
						i = { field: f.id, inputType: 'select', selectValue: '' };
						break;
					}
					case 'textfield': {
						i = { field: f.id, inputType: 'string', stringValue: '' };
						break;
					}
					case 'checkbox': {
						i = { field: f.id, inputType: 'boolean', booleanValue: false };
						break;
					}
					case 'multitext': {
						i = { field: f.id, inputType: 'multitext', multitextValue: [] };
						break;
					}
					case 'numberfield': {
						i = { field: f.id, inputType: 'number', numberValue: 0 };
						break;
					}
					case 'singleline-multitext': {
						i = { field: f.id, inputType: 'multitext', multitextValue: [] };
						break;
					}
					case 'singleline-textfield': {
						i = { field: f.id, inputType: 'string', stringValue: '' };
						break;
					}
					case 'url': {
						i = { field: f.id, inputType: 'string', stringValue: '' };
						break;
					}
					case 'hidden': {
						i = { field: f.id, inputType: 'string', stringValue: '' };
						break;
					}
				}
				return i;
			});
			newEntries.forEach((e) => {
				if (existing.length === 0) {
					existing.push(e);
				} else {
					let i = existing.findIndex((f) => f.field === e.field);
					if (i === -1) existing.push(e);
				}
			});
		},
		async generateIndexData() {
			const owner = this.owner!;
			if (!this.owner) throw new Error('expected owner');
			this.entity.indexData.ownerActive = owner.active;
			if (this.entity.sort) {
				this.entity.indexData.sortActive = this.sort.status === 'accepted';
				if (this.sort && this.sort.breedingmethod)
					this.entity.indexData.sortBreedingmethod = this.sort.breedingmethod;
			} else {
				this.entity.indexData.sortActive = true;
			}
			let accepted: CountryUtil.CountryCode[] = [];
			if (owner.staticData.countrySpecific) {
				let countryKeys = Object.keys(
					owner.staticData.countrySpecific,
				) as CountryUtil.CountryCode[];
				for (let country of countryKeys) {
					if (
						owner.staticData.countrySpecific[country]?.autApprovalStatus === 'accepted'
					) {
						accepted.push(country);
					}
				}
			}
			this.entity.indexData.ownerAcceptedInCountries = accepted;
			OfferUtil.updateApprovalHistory(this.entity);
			if(this.entity.sort) {
				const sortEnt = await useRelatedStore().addEntity(this.entity.sort) as EAV.Entity<Sort.Sort>;
				this.entity.indexData.seedFlags = sortEnt.data.seedFlags || [];
			}
		},
		openSortDrawer(mixNum?: number) {
			if (!this.entity.species) return;
			if (this.sortDrawerOpen) this.sortDrawerOpen = false;
			this.sortResultTarget = typeof mixNum === 'undefined' ? -1 : mixNum;
			this.sortDrawerOpen = true;
		},
		sortSelectResult(sort: SortModel) {
			if (this.sortResultTarget < 0) {
				this.entity.sort = sort.getId();
				this.sort = sort;
			} else {
				this.entity.mixContents![this.sortResultTarget].sort = sort.getId();
			}
			this.sortDrawerOpen = false;
		},
	},
});
